import { Box, Button, dateFormat, IconFont, Text } from '@byecode/ui'
import type { CalendarMode } from '@lighthouse/core'
import { CALENDAR_MODE } from '@lighthouse/core'
import { addDays, addMonths, lightFormat, startOfMonth, startOfWeek } from 'date-fns'
import React, { forwardRef, useMemo } from 'react'
import styled from 'styled-components'

import { useLanguageContext } from '../../../../../contexts'
import type { CalendarStyles } from './types'

interface CalendarHeaderProps {
    mode?: CalendarMode
    weekShowCount?: number
    date?: Date
    styles?: CalendarStyles
    onPrevYear?: (date: Date) => void
    onPrevMonth?: (date: Date) => void
    onNextYear?: (date: Date) => void
    onNextMonth?: (date: Date) => void
}

const SCxHeader = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    padding: 0 10px;
    height: 40px;
`

const SCxTitle = styled(Text)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    justify-content: center;
    text-align: center;
    font-size: 14px;
    line-height: 22px;
`

const SCxWeekList = styled.div`
    display: flex;
    height: 32px;
    line-height: 32px;
`

export const CalendarHeader = forwardRef<HTMLDivElement, CalendarHeaderProps>((props, ref) => {
    const { mode, weekShowCount, date = new Date(), onPrevYear, onPrevMonth, onNextYear, onNextMonth, styles, ...rest } = props

    const isWeekMode = mode === CALENDAR_MODE.week

    const { language } = useLanguageContext()

    const len = isWeekMode ? weekShowCount ?? 7 : 7

    const headers = useMemo(() => {
        const startIndex = isWeekMode ? 3 : 0
        const startDate = isWeekMode
            ? date
            : startOfWeek(startOfMonth(date), {
                  weekStartsOn: 0
              })
        const labels: React.ReactNode[] = isWeekMode ? ['今天', '明天', '后天'] : []
        for (let i = startIndex; i < 7; i++) {
            labels.push(dateFormat(addDays(startDate, i), 'iiiiii', { locale: language }))
        }
        return labels
    }, [date, isWeekMode, language])

    return (
        <>
            {isWeekMode ? null : (
                <SCxHeader ref={ref} {...rest}>
                    <Box>
                        <Button
                            size="sm"
                            type="text"
                            icon={<IconFont type="ArrowLeftDouble" color={styles?.header.iconColor} />}
                            onClick={() => onPrevYear?.(addMonths(date, -12))}
                        />
                        <Button
                            size="sm"
                            type="text"
                            icon={<IconFont type="ArrowLeftSmall" color={styles?.header.iconColor} />}
                            onClick={() => onPrevMonth?.(addMonths(date, -1))}
                        />
                    </Box>

                    <SCxTitle color={styles?.header.fontColor} style={{ fontFamily: styles?.header.fontFamily }}>
                        <Button size="sm" type="text" style={{ color: styles?.header.fontColor }}>
                            {lightFormat(date, 'yyyy')}
                        </Button>
                        <span>-</span>
                        <Button size="sm" type="text" style={{ color: styles?.header.fontColor }}>
                            {lightFormat(date, 'MM')}
                        </Button>
                    </SCxTitle>
                    <Box>
                        <Button
                            size="sm"
                            type="text"
                            icon={<IconFont type="ArrowRightSmall" color={styles?.header.iconColor} />}
                            onClick={() => onNextMonth?.(addMonths(date, 1))}
                        />
                        <Button
                            size="sm"
                            type="text"
                            icon={<IconFont type="ArrowRightDouble" color={styles?.header.iconColor} />}
                            onClick={() => onNextYear?.(addMonths(date, 12))}
                        />
                    </Box>
                </SCxHeader>
            )}
            <SCxWeekList style={{ fontFamily: styles?.headerWeek.fontFamily }}>
                {headers.map((label, i) => (
                    <Text align="center" key={i} color={styles?.headerWeek.fontColor} styles={{ root: { flex: `0 0 ${100 / len}%` } }}>
                        {label}
                    </Text>
                ))}
            </SCxWeekList>
        </>
    )
})
